import { normalizeOrderCoupon } from '../utils/orderCoupon'
import { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { Helmet, HelmetProvider } from 'react-helmet-async'
import api from '../api'
import { ReactComponent as CheckIcon } from '../assets/images/icons/ic-check-circle.svg'
import { ReactComponent as DocumentIcon } from '../assets/images/icons/ic-document.svg'
import { ReactComponent as OpenIcon } from '../assets/images/icons/ic-open.svg'
import { FileType } from '../common/constants'
import MainContext from '../common/MainContext'
import { canAccessPage } from '../common/permissions'
import StatusBadge from '../components/StatusBadge'
import Card from '../components/cards/Card'
import Button from '../components/Button'
import EmptyContent from '../components/EmptyContent'
import HeaderFooterLayout from '../components/layouts/HeaderFooterLayout'
import MeatBallsMenu from '../components/MeatBallsMenu'
import Table from '../components/Table'
import TextInput from '../components/TextInput'
import Uploader from '../components/Uploader'
import Dialog from '../components/dialogs/Dialog'
import { formatDateV2, formatPrice, formatTimeV2 } from '../utils'
import styles from './InvoiceRequests.module.css'

const STATUS_LABELS = {
    succeeded: 'Pagato',
    refunded: 'Rimborsato',
    partially_refunded: 'Parzialmente rimborsato',
    partially_paid: 'Parzialmente pagato',
    pending: 'In attesa',
    unpaid: 'Non pagato',
    abandoned: 'Annullato',
}

const formatAmount = (amount) => `€ ${formatPrice((amount || 0) / 100)}`

const formatDateTime = (value) => value
    ? `${formatDateV2(value, { day: '2-digit', month: '2-digit', year: 'numeric' }, 'it', true, true)} · ${formatTimeV2(value)}`
    : '—'

const formatPaymentMethod = (value) => {
    if (!value) return 'Non specificato'
    if (value === 'customer_balance') return 'Bonifico'
    return String(value)
        .split('_')
        .map((part) => part ? `${part.charAt(0).toUpperCase()}${part.slice(1)}` : '')
        .join(' ')
}

const DetailField = ({ label, value, full = false }) => (
    <div className={`${styles.detailField} ${full ? styles.fullField : ''}`}>
        <span>{label}</span>
        <strong>{value || '—'}</strong>
    </div>
)

const InvoiceDocumentPreview = ({ preview }) => {
    const fileName = (() => {
        try {
            return decodeURIComponent(preview.split('?')[0].split('/').pop())
        } catch {
            return 'Documento fattura'
        }
    })()

    return (
        <div className={styles.documentPreview}>
            <DocumentIcon />
            <div>
                <strong>{fileName || 'Documento fattura'}</strong>
                <span>Documento attualmente caricato</span>
            </div>
            <button
                type='button'
                onClick={(event) => {
                    event.preventDefault()
                    event.stopPropagation()
                    window.open(preview, '_blank', 'noopener,noreferrer')
                }}
            >
                Apri <OpenIcon />
            </button>
        </div>
    )
}

const InvoiceRequests = () => {
    const context = useContext(MainContext)
    const [requests, setRequests] = useState([])
    const [loading, setLoading] = useState(true)
    const [query, setQuery] = useState('')
    const [statusFilter, setStatusFilter] = useState('pending')
    const [selectedRequest, setSelectedRequest] = useState(null)
    const [uploadReset, setUploadReset] = useState(0)
    const [updating, setUpdating] = useState(false)
    const canEdit = canAccessPage(context.user, 'invoices', 'edit')

    const loadRequests = useCallback(async () => {
        setLoading(true)
        try {
            const data = await api.get('/admin/invoice-requests')
            setRequests(Array.isArray(data) ? data : [])
        } catch (error) {
            console.error(error)
        } finally {
            setLoading(false)
        }
    }, [])

    useEffect(() => {
        loadRequests()
    }, [loadRequests])

    const updateRequest = useCallback(async (requestId, changes) => {
        const updated = await api.put(`/admin/invoice-requests/${requestId}`, changes)
        setRequests((current) => current.map((request) => (
            request.id === requestId ? { ...request, ...updated } : request
        )))
        setSelectedRequest((current) => (
            current?.id === requestId ? { ...current, ...updated } : current
        ))
        return updated
    }, [])

    const markAsFulfilled = useCallback(async (request) => {
        if (!request || request.fulfilled_at || updating) return
        setUpdating(true)
        try {
            await updateRequest(request.id, { fulfilled: true })
        } catch (error) {
            console.error(error)
        } finally {
            setUpdating(false)
        }
    }, [updateRequest, updating])

    const completeInvoiceUpload = useCallback(async (files) => {
        const fileUrl = files?.[0]?.url
        if (!fileUrl || !selectedRequest) return
        setUpdating(true)
        try {
            await updateRequest(selectedRequest.id, { file_url: fileUrl })
            setUploadReset((current) => current + 1)
        } catch (error) {
            console.error(error)
        } finally {
            setUpdating(false)
        }
    }, [selectedRequest, updateRequest])

    const openDetails = useCallback((request) => {
        setSelectedRequest(request)
        setUploadReset((current) => current + 1)
    }, [])

    const closeDetails = useCallback(() => {
        setSelectedRequest(null)
    }, [])

    const filteredRequests = useMemo(() => {
        const normalizedQuery = query.trim().toLocaleLowerCase('it-IT')
        return requests.filter((request) => {
            if (statusFilter === 'pending' && request.fulfilled_at) return false
            if (statusFilter === 'fulfilled' && !request.fulfilled_at) return false
            if (!normalizedQuery) return true

            return [
                request.order_no,
                request.user?.name,
                request.user?.surname,
                request.user?.email,
                request.first_name,
                request.last_name,
                request.company_name,
                request.tax_code,
                request.vat_number,
            ].some((value) => String(value || '').toLocaleLowerCase('it-IT').includes(normalizedQuery))
        })
    }, [query, requests, statusFilter])

    const tableRows = useMemo(() => filteredRequests.map((request) => {
        const userName = `${request.user?.name || ''} ${request.user?.surname || ''}`.trim()
        const menuActions = [{
            label: 'Dettagli',
            icon: OpenIcon,
            onClick: () => openDetails(request),
        }]

        if (canEdit && !request.fulfilled_at) {
            menuActions.push({
                label: 'Segna come evasa',
                icon: CheckIcon,
                onClick: () => markAsFulfilled(request),
            })
        }

        if (request.file_url) {
            menuActions.push({
                label: 'Apri documento',
                icon: OpenIcon,
                onClick: () => window.open(request.file_url, '_blank', 'noopener,noreferrer'),
            })
        }

        return {
            ...request,
            user_name: userName,
            order_reference: {
                value: request.order_no,
                type: 'string',
                component: (
                    <div className={styles.orderReference}>
                        <strong>#{request.order_no}</strong>
                        {request.installment_number && <span>Rata {request.installment_number}</span>}
                    </div>
                ),
            },
            formatted_amount: formatAmount(request.amount),
            processing_status: {
                value: request.fulfilled_at ? 'fulfilled' : 'pending',
                type: 'string',
                component: (
                    <StatusBadge
                        appearance='soft'
                        label={request.fulfilled_at ? 'Evasa' : 'Da evadere'}
                        color={request.fulfilled_at ? 'var(--sf-green)' : 'var(--sf-orange)'}
                    />
                ),
            },
            actions: {
                value: request.id,
                type: 'number',
                component: <MeatBallsMenu actions={menuActions} />,
            },
        }
    }), [canEdit, filteredRequests, markAsFulfilled, openDetails])

    const pendingCount = requests.filter((request) => !request.fulfilled_at).length
    const fulfilledCount = requests.length - pendingCount
    const selectedOrder = selectedRequest?.order
    const selectedCoupon = normalizeOrderCoupon(selectedOrder?.coupon)
    const selectedInstallment = selectedRequest?.installment
    const billingName = selectedRequest?.subject_type === 'company'
        ? selectedRequest?.company_name
        : `${selectedRequest?.first_name || ''} ${selectedRequest?.last_name || ''}`.trim()

    return (
        <HeaderFooterLayout>
            <HelmetProvider>
                <Helmet><title>Fatture</title></Helmet>
            </HelmetProvider>
            <div className={styles.container}>
                <div className={styles.section}>
                    <div className={styles.sectionInner}>
                        <header className={styles.header}>
                            <div>
                                <p className={styles.eyebrow}>Amministrazione</p>
                                <h1>Fatture</h1>
                                <p className={styles.subtitle}>Richieste relative a ordini e rate con pagamento completato.</p>
                            </div>
                            <div className={styles.summary}>
                                <span><strong>{loading ? '—' : pendingCount}</strong> da evadere</span>
                                <span><strong>{loading ? '—' : fulfilledCount}</strong> evase</span>
                            </div>
                        </header>

                        <Card appearance="modern" integratedTable title="Richieste di fatturazione" actions={
                            <span className={styles.resultCount} role="status">{loading ? 'Caricamento…' : `${filteredRequests.length} richieste`}</span>
                        }>
                            <div className={styles.toolbar}>
                                <label className={styles.searchField}>
                                    <span className={styles.visuallyHidden}>Cerca richieste di fatturazione</span>
                                    <TextInput appearance='modern' type='search' placeholder='Cerca per utente, ordine o dati fiscali' value={query} onKeyUp={setQuery} />
                                </label>
                                <div className={styles.filters} role="group" aria-label="Stato delle richieste">
                                    {[
                                        { value: 'pending', label: 'Da evadere' },
                                        { value: 'fulfilled', label: 'Evase' },
                                        { value: 'all', label: 'Tutte' },
                                    ].map((filter) => (
                                        <button
                                            type="button"
                                            aria-pressed={statusFilter === filter.value}
                                            key={filter.value}
                                            className={statusFilter === filter.value ? styles.activeFilter : ''}
                                            onClick={() => setStatusFilter(filter.value)}
                                        >
                                            {filter.label}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            <Table
                                appearance='minimal'
                                style={{ border: 0, borderRadius: 0, boxShadow: 'none', marginTop: 0 }}
                                loading={loading}
                                loadingN={8}
                                defaultSort={{ key: 'created_at', direction: 'desc' }}
                                onRowClick={(request) => openDetails(request)}
                                columns={[
                                    { key: 'created_at', label: 'DATA', type: 'date' },
                                    { key: 'user_name', label: 'NOME E COGNOME', type: 'string' },
                                    { key: 'order_reference', label: 'ORDINE', type: 'custom' },
                                    { key: 'formatted_amount', label: 'IMPORTO', type: 'string' },
                                    { key: 'processing_status', label: 'STATO DI LAVORAZIONE', type: 'custom' },
                                    { key: 'actions', label: 'AZIONI', type: 'custom', handleClick: true },
                                ]}
                                elements={tableRows}
                            />

                            {!loading && tableRows.length === 0 && (
                                <EmptyContent
                                    title='Nessuna richiesta trovata'
                                    message='Non ci sono richieste di fatturazione corrispondenti ai filtri selezionati.'
                                />
                            )}
                        </Card>
                    </div>
                </div>
            </div>

            <Dialog
                appearance="modern"
                open={Boolean(selectedRequest)}
                title={selectedRequest ? `Richiesta fattura · Ordine #${selectedRequest.order_no}` : 'Richiesta fattura'}
                onClose={closeDetails}
                style={{ width: 'min(920px, 94vw)' }}
                contentMaxHeight='72vh'
                action={selectedRequest && (
                    <div className={styles.dialogActions}>
                        {!selectedRequest.fulfilled_at && (
                            <Button requiresEdit disabled={!canEdit || updating} onClick={() => markAsFulfilled(selectedRequest)}>
                                Segna come evasa
                            </Button>
                        )}
                    </div>
                )}
            >
                {selectedRequest && (
                    <div className={styles.detailDialog}>
                        <div className={styles.detailHeading}>
                            <StatusBadge
                                appearance='soft'
                                label={selectedRequest.fulfilled_at ? 'Evasa' : 'Da evadere'}
                                color={selectedRequest.fulfilled_at ? 'var(--sf-green)' : 'var(--sf-orange)'}
                            />
                            <span>Richiesta del {formatDateTime(selectedRequest.created_at)}</span>
                        </div>

                        <section className={styles.invoiceAmount} aria-label='Importo da fatturare'>
                            <span className={styles.invoiceAmountLabel}>Importo da fatturare</span>
                            <strong className={styles.invoiceAmountValue}>{formatAmount(selectedRequest.amount)}</strong>
                            <span className={styles.invoiceAmountReference}>
                                {selectedInstallment
                                    ? `Rata ${selectedInstallment.number} · Ordine #${selectedRequest.order_no} · Totale ordine ${formatAmount(selectedOrder?.amount)}`
                                    : `Totale ordine #${selectedRequest.order_no}`}
                            </span>
                        </section>

                        <section className={styles.detailSection}>
                            <h3>Intestatario fattura</h3>
                            <div className={styles.detailGrid}>
                                <DetailField label={selectedRequest.subject_type === 'company' ? 'Ragione sociale' : 'Nome e cognome'} value={billingName} />
                                <DetailField label='Tipologia' value={selectedRequest.subject_type === 'company' ? 'Azienda' : 'Persona fisica'} />
                                <DetailField label='Codice fiscale' value={selectedRequest.tax_code} />
                                <DetailField label='Partita IVA' value={selectedRequest.vat_number} />
                                <DetailField label='Email' value={selectedRequest.email} />
                                <DetailField label='SDI' value={selectedRequest.recipient_code} />
                                <DetailField label='PEC' value={selectedRequest.pec} />
                                <DetailField
                                    label='Indirizzo'
                                    value={`${selectedRequest.address}, ${selectedRequest.postal_code} ${selectedRequest.city} (${selectedRequest.province}), ${selectedRequest.country}`}
                                    full
                                />
                            </div>
                        </section>

                        <section className={styles.detailSection}>
                            <div className={styles.sectionTitleRow}>
                                <h3>Ordine #{selectedRequest.order_no}</h3>
                                <button className={styles.linkButton} onClick={() => window.open(`/orders/${selectedRequest.order_id}`, '_blank')}>Apri ordine</button>
                            </div>
                            <div className={styles.detailGrid}>
                                <DetailField label='Utente' value={`${selectedRequest.user?.name || ''} ${selectedRequest.user?.surname || ''}`.trim()} />
                                <DetailField label='Email utente' value={selectedRequest.user?.email} />
                                <DetailField label='Stato ordine' value={STATUS_LABELS[selectedOrder?.status] || selectedOrder?.status} />
                                <DetailField label='Totale ordine' value={formatAmount(selectedOrder?.amount)} />
                                <DetailField label='Codice sconto' value={selectedCoupon?.code || 'Nessuno'} />
                                {selectedCoupon && (
                                    <DetailField
                                        label='Valore sconto'
                                        value={selectedCoupon.amountOff !== null
                                            ? formatAmount(selectedCoupon.amountOff)
                                            : selectedCoupon.percentOff !== null ? `${selectedCoupon.percentOff}%` : null}
                                    />
                                )}
                                <DetailField label='Creato il' value={formatDateTime(selectedOrder?.created_at)} />
                                <DetailField label='Completato il' value={formatDateTime(selectedOrder?.completed_at)} />
                                <DetailField label='Metodo di pagamento' value={formatPaymentMethod(selectedOrder?.payment_method)} />
                                <DetailField label='Payment intent' value={selectedOrder?.intent_id} />
                            </div>
                        </section>

                        {selectedInstallment && (
                            <section className={styles.detailSection}>
                                <h3>Rata {selectedInstallment.number} oggetto della richiesta</h3>
                                <div className={styles.detailGrid}>
                                    <DetailField label='Importo rata' value={formatAmount(selectedInstallment.amount)} />
                                    <DetailField label='Stato' value={STATUS_LABELS[selectedInstallment.status] || selectedInstallment.status} />
                                    <DetailField label='Metodo di pagamento' value={formatPaymentMethod(selectedInstallment.payment_method)} />
                                    <DetailField label='Scadenza' value={formatDateTime(selectedInstallment.expired_at)} />
                                    <DetailField label='Completata il' value={formatDateTime(selectedInstallment.completed_at)} />
                                    <DetailField label='Payment intent' value={selectedInstallment.intent_id} />
                                </div>
                            </section>
                        )}

                        <section className={styles.detailSection}>
                            <h3>Prodotti</h3>
                            <div className={styles.productList}>
                                {(selectedOrder?.products || []).map((product) => (
                                    <div className={styles.productRow} key={product.id}>
                                        <div>
                                            <strong>{product.name}</strong>
                                            <span>{String(product.type || '').replaceAll('_', ' ')}</span>
                                        </div>
                                        <strong>{formatAmount(product.discounted_price ?? product.price)}</strong>
                                    </div>
                                ))}
                                {(selectedOrder?.products || []).length === 0 && <span className={styles.muted}>Nessun prodotto associato.</span>}
                            </div>
                        </section>

                        {(selectedOrder?.installments || []).length > 0 && (
                            <section className={styles.detailSection}>
                                <h3>Piano rateale</h3>
                                <div className={styles.installmentList}>
                                    {selectedOrder.installments.map((installment) => (
                                        <div
                                            className={`${styles.installmentRow} ${installment.id === selectedRequest.installment_id ? styles.selectedInstallment : ''}`}
                                            key={installment.id}
                                        >
                                            <strong>Rata {installment.number}</strong>
                                            <span>{formatAmount(installment.amount)}</span>
                                            <span>{STATUS_LABELS[installment.status] || installment.status}</span>
                                            <span>{formatDateTime(installment.completed_at || installment.expired_at)}</span>
                                        </div>
                                    ))}
                                </div>
                            </section>
                        )}

                        <section className={styles.detailSection}>
                            <h3>Lavorazione</h3>
                            <div className={styles.detailGrid}>
                                <DetailField label='Stato' value={selectedRequest.fulfilled_at ? 'Evasa' : 'Da evadere'} />
                                <DetailField label='Evasa il' value={formatDateTime(selectedRequest.fulfilled_at)} />
                                <DetailField
                                    label='Evasa da'
                                    value={selectedRequest.fulfilled_by
                                        ? `${selectedRequest.fulfilled_by.name} ${selectedRequest.fulfilled_by.surname} · ${selectedRequest.fulfilled_by.email}`
                                        : null}
                                    full
                                />
                                <DetailField label='Documento' value={selectedRequest.file_url ? 'Caricato' : 'Non caricato'} />
                            </div>
                        </section>

                        <section className={`${styles.detailSection} ${styles.uploaderSection}`}>
                            <h3>Documento fattura</h3>
                            {canEdit ? (
                                <Uploader
                                    type={FileType.All}
                                    multiple={false}
                                    reset={uploadReset}
                                    preview={selectedRequest.file_url}
                                    Preview={InvoiceDocumentPreview}
                                    subdirectory={`invoices/${selectedRequest.id}`}
                                    label={selectedRequest.file_url ? 'CAMBIA FILE' : 'SCEGLI FILE'}
                                    message={selectedRequest.file_url
                                        ? 'Trascina o seleziona un nuovo file per sostituire il documento.'
                                        : 'Trascina o seleziona il file della fattura.'}
                                    onComplete={completeInvoiceUpload}
                                />
                            ) : selectedRequest.file_url ? (
                                <InvoiceDocumentPreview preview={selectedRequest.file_url} />
                            ) : (
                                <span className={styles.muted}>Nessun documento caricato.</span>
                            )}
                        </section>
                    </div>
                )}
            </Dialog>
        </HeaderFooterLayout>
    )
}

export default InvoiceRequests
